import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Circle,
  Loader2,
  Play,
  RotateCw,
  Search,
} from 'lucide-react';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import {
  autoCreateRouterForward,
  checkRouterConnectivity,
  clearRouterHostDesignation,
  designateRouterHost,
  ezvizBootstrap,
  importRouterCameras,
  probeZoneRouter,
  type RouterAutoForwardResponse,
  type RouterCheckKind,
  type RouterCheckResponse,
  type RouterCheckTarget,
  type RouterHostDesignation,
  type RouterImportCameraResult,
  type RouterProbeForward,
  type RouterProbeLanHost,
  type RouterProbeOk,
  type RouterProbeResponse,
} from '@/api/zoneRouter';
import { detectCameraResolution } from '@/api/cameras';
import { detectProviderCodec } from '@/api/providers';
import { getCameraSnapshot } from '@/api/cameraSnapshot';
import { extractApiError, extractApiErrorLocalised } from '@/utils/apiError';
import { translatePortForwardError } from './portForwardErrors';
import { CAMERA_TYPE_OPTIONS } from './helpers';
import {
  DiscoveredPeerBulkFooter,
  DiscoveredPeerList,
  effectiveKind,
  type DiscoveredPeer,
} from './DiscoveredPeerList';

interface ButtonProps {
  zoneId: number | string;
  /** Operator-facing model_name on the router record. We gate the
   *  button visibility on this — Phase A is Keenetic only. */
  modelName: string;
  /** Fires whenever a probe attempt finishes — success, logical
   *  failure (`{ok:false}`), OR transport error. */
  onProbed: (result: RouterProbeResponse | null, transportError: string | null) => void;
  /** Fires when probing starts so the parent can show a "loading"
   *  hint near the header badge. */
  onProbingChange?: (probing: boolean) => void;
}

/**
 * Router-autodiscovery Phase A trigger button.
 *
 * 2026-06-02 redesign: the result rendering lives in `<RouterProbeContent>`
 * below — this component is the optional kick-off CTA (the Router card
 * surfaces the same action via a refresh icon). Kept around for the
 * "no router yet, here's a button" path on empty-state surfaces.
 */
export function RouterAutodiscoverButton({ zoneId, modelName, onProbed, onProbingChange }: ButtonProps) {
  const { t } = useT();
  const [probing, setProbing] = useState(false);

  // Vendor gate. Phase A handles Keenetic only.
  if ((modelName || '').toLowerCase() !== 'keenetic') return null;

  const onClick = async () => {
    if (probing) return;
    setProbing(true);
    onProbingChange?.(true);
    onProbed(null, null);
    try {
      const r = await probeZoneRouter(zoneId);
      onProbed(r.data, null);
    } catch (err) {
      onProbed(
        null,
        extractApiError(
          isAxiosError(err) ? err : undefined,
          err instanceof Error ? err.message : 'unknown error',
        ),
      );
    } finally {
      setProbing(false);
      onProbingChange?.(false);
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={onClick} disabled={probing}>
      {probing ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Search className="size-3.5" />
      )}
      {t('equipment.router.autodiscover')}
    </Button>
  );
}

/** Pretty WG state pill — kept exported for legacy call-sites. */
export function RouterWgPill({ probe }: { probe: RouterProbeOk }) {
  const { t } = useT();
  const wgReady = probe.wg_state === 'configured' && !!probe.wg_uplink_ip;
  const wrong = probe.wg_state === 'wrong_scope';
  const tone = wgReady
    ? 'bg-green-500/15 text-green-700 dark:text-green-400'
    : wrong
      ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
      : 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
  const label = wgReady
    ? `WG ✓ ${probe.wg_uplink_ip}`
    : wrong
      ? t('equipment.router.probe.wgWrongScope')
      : t('equipment.router.probe.wgAbsent');
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${tone}`}
    >
      {label}
    </span>
  );
}

/** Transport-error banner. */
export function RouterProbeTransportError({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

export function RouterProbeErrorRow({ error, detail }: { error: string; detail: string }) {
  const { t } = useT();
  const label =
    error === 'credentials_rejected'
      ? t('equipment.router.probe.credentialsRejected')
      : error === 'unreachable'
        ? t('equipment.router.probe.unreachable')
        : error === 'enumerate_failed'
          ? t('equipment.router.probe.enumerateFailed')
          : error;
  return (
    <div className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <div className="space-y-0.5">
        <div className="font-medium">{label}</div>
        {detail && <div className="text-xs opacity-80">{detail}</div>}
      </div>
    </div>
  );
}

/**
 * Mint a UUID4 for the import-cameras idempotency key.
 *
 * Uses `crypto.randomUUID()` where the runtime exposes it (every modern
 * browser since 2023, and all browsers we support on the production
 * dashboard target). Falls back to a Math.random-based v4 generator in
 * the rare environment without crypto — the BE only uses the key for
 * dedup, so the lower-entropy fallback is fine even though it's not
 * cryptographically random.
 */
function mintUuid(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') {
    try {
      return c.randomUUID();
    } catch {
      // Fall through to manual generator.
    }
  }
  // RFC4122 v4 fallback. Not cryptographically random — adequate for
  // idempotency dedup but should not be used as a security primitive.
  let result = '';
  for (let i = 0; i < 32; i++) {
    let r = Math.floor(Math.random() * 16);
    if (i === 12) r = 4;
    else if (i === 16) r = (r & 0x3) | 0x8;
    result += r.toString(16);
    if (i === 7 || i === 11 || i === 15 || i === 19) result += '-';
  }
  return result;
}

/**
 * Determine if a host looks like a Linza-owned jetson — heuristic on
 * hostname so the per-host import button stays hidden for hosts the
 * operator doesn't need to forward ports to. Conservative on purpose:
 * unknown hostnames don't trigger the suppression.
 */
function isJetsonHost(h: RouterProbeLanHost): boolean {
  const hn = (h.host_name || '').toLowerCase();
  if (!hn) return false;
  return /jetson|nvidia|linza-edge|linza-jet|nano-/.test(hn);
}

/**
 * Map a RouterProbeLanHost + forward join into the shared peer shape.
 * Pre-computed once per probe so the list re-renders are cheap.
 */
function buildPeers(
  hosts: RouterProbeLanHost[],
  forwards: RouterProbeForward[],
): DiscoveredPeer[] {
  // Index forwards by lowercased to_host for the join.
  const byKey = new Map<string, RouterProbeForward[]>();
  for (const fwd of forwards) {
    const key = (fwd.to_host || '').toLowerCase().trim();
    if (!key) continue;
    const bucket = byKey.get(key);
    if (bucket) bucket.push(fwd);
    else byKey.set(key, [fwd]);
  }
  return hosts.map(host => {
    const candidates: RouterProbeForward[] = [];
    const ipKey = (host.ip || '').toLowerCase();
    const macKey = (host.mac || '').toLowerCase();
    if (ipKey && byKey.has(ipKey)) candidates.push(...byKey.get(ipKey)!);
    if (macKey && byKey.has(macKey)) candidates.push(...byKey.get(macKey)!);
    const seen = new Set<string>();
    const unique = candidates.filter(fwd => {
      const k = `${fwd.interface}:${fwd.external_port}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    const rtsp = unique
      .filter(fwd => fwd.internal_port === 554 && (fwd.external_port ?? 0) > 0)
      .sort((a, b) => (a.external_port ?? 0) - (b.external_port ?? 0));
    const importable = rtsp[0] || null;
    const others = unique.filter(fwd => fwd !== importable);

    return {
      mac: host.mac,
      ip: host.ip,
      host_name: host.host_name,
      active: host.active,
      kind: host.kind,
      designation: host.designation ?? null,
      imported_as_camera_id: host.imported_as_camera_id ?? null,
      imported_as_camera_name: host.imported_as_camera_name ?? null,
      imported_via_nvr_id: host.imported_via_nvr_id ?? null,
      is_jetson: isJetsonHost(host),
      importable_forward: importable,
      other_forwards: others,
    } satisfies DiscoveredPeer;
  });
}

/**
 * Inline probe-result panel — split into the two subsections from the
 * spec (suggested cameras + "Остальные устройства"), each driven by the
 * shared `DiscoveredPeerList` primitive.
 *
 * Import flow per-row:
 *   1. Existing 554 forward → confirm modal asks for credentials.
 *   2. No forward → confirm modal calls the auto-allocate endpoint,
 *      then continues to the credentials step.
 *      Group A still pending? we toast it + show the manual fallback
 *      path via the AddPortForwardDialog.
 *   3. `no_reachable_path` → inline destructive banner, no modal.
 *
 * Bulk flow:
 *   - Toggle bulk-mode → checkbox per row.
 *   - Footer offers "Импортировать как камеры" + "Создать пробросы 554".
 */
export function RouterProbeContent({
  probe,
  zoneId,
  onCamerasImported,
}: {
  probe: RouterProbeOk;
  zoneId: number | string;
  onCamerasImported?: () => void;
}) {
  const { t, lang } = useT();
  const wgReady = probe.wg_state === 'configured' && !!probe.wg_uplink_ip;

  // Local optimistic-override map keyed on MAC. When the operator picks
  // a designation in the three-dots menu we apply the change immediately
  // here so the next render reflects the new state — without waiting for
  // a fresh /probe/ round-trip (finding #14 — operator clicked "Это NVR"
  // and the menu still showed nothing checked until they manually hit
  // Refresh). null = reset/cleared (BE will drop the row on next probe).
  const [localDesignations, setLocalDesignations] = useState<
    Map<string, RouterHostDesignation | null>
  >(() => new Map());

  const peers = useMemo(() => {
    const base = buildPeers(probe.lan_hosts, probe.existing_forwards || []);
    if (localDesignations.size === 0) return base;
    return base.map(p => {
      if (!localDesignations.has(p.mac)) return p;
      const override = localDesignations.get(p.mac);
      return { ...p, designation: override ?? null };
    });
  }, [probe.lan_hosts, probe.existing_forwards, localDesignations]);

  // Split into "candidates" vs "everything else" — candidates are
  // anything the BE classified OR the operator designated as a camera
  // or NVR; everything else lives in the secondary subsection.
  const candidates = useMemo(
    () => peers.filter(p => {
      const k = effectiveKind(p);
      return k === 'camera' || k === 'nvr';
    }),
    [peers],
  );
  const others = useMemo(
    () => peers.filter(p => {
      const k = effectiveKind(p);
      return k !== 'camera' && k !== 'nvr';
    }),
    [peers],
  );

  // Bulk state — separate sets per subsection so toggling on one list
  // doesn't bleed into the other; keeps the UX legible.
  const [bulkA, setBulkA] = useState(false);
  const [selectedA, setSelectedA] = useState<Set<string>>(() => new Set());
  const [bulkB, setBulkB] = useState(false);
  const [selectedB, setSelectedB] = useState<Set<string>>(() => new Set());

  // Pending per-host import (single confirm). Drives the confirm modal.
  const [importTarget, setImportTarget] = useState<{
    peer: DiscoveredPeer;
    mode: 'existing-forward' | 'auto-forward';
  } | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<RouterImportCameraResult | null>(null);

  // Credentials are left blank on the per-host confirm dialog (finding
  // #13). The operator fills them in the Camera edit dialog after the
  // import — the confirm modal pre-fills here would silently pre-bake
  // the router admin's username onto each camera (regression noted in
  // the 2026-06-02 test report).
  const [credUsername, setCredUsername] = useState('');
  const [credPassword, setCredPassword] = useState('');

  // UUID4 minted per confirm-dialog session so a double-click / submit
  // race / network retry can't create duplicate Camera rows. BE-A's
  // dedup-by-key honours the same value on resubmit (finding #3).
  const [importIdempotencyKey, setImportIdempotencyKey] = useState<string>('');

  // Source type for the imported camera — the same "Тип источника"
  // picker as the edit dialog. Default `rtsp` keeps the legacy one-click
  // import; selecting `ezviz` swaps the confirm body for the SDK-
  // bootstrap retrieval card (forwards → enable RTSP → probe).
  const [cameraType, setCameraType] = useState<string>('rtsp');

  const toggleSelectA = (mac: string) => {
    setSelectedA(prev => {
      const next = new Set(prev);
      if (prev.has(mac)) next.delete(mac);
      else next.add(mac);
      return next;
    });
  };
  const toggleSelectB = (mac: string) => {
    setSelectedB(prev => {
      const next = new Set(prev);
      if (prev.has(mac)) next.delete(mac);
      else next.add(mac);
      return next;
    });
  };

  const startImport = (peer: DiscoveredPeer) => {
    setImportError(null);
    setImportResult(null);
    // Reset camera credentials on every open — they're per-host and the
    // confirm modal should never pre-fill the router admin's username.
    setCredUsername('');
    setCredPassword('');
    setCameraType('rtsp');
    // Mint a fresh UUID4 so each "open the dialog" run gets its own
    // idempotency key. Retries inside this open keep the same value.
    setImportIdempotencyKey(mintUuid());
    setImportTarget({
      peer,
      mode: peer.importable_forward ? 'existing-forward' : 'auto-forward',
    });
  };

  const doDesignate = async (peer: DiscoveredPeer, designation: RouterHostDesignation) => {
    // Optimistic — apply immediately so the menu re-render shows the
    // checkmark right away (finding #14). Reverted on BE error below.
    setLocalDesignations(prev => {
      const next = new Map(prev);
      next.set(peer.mac, designation);
      return next;
    });
    const resp = await designateRouterHost(zoneId, {
      lan_host_ip: peer.ip,
      lan_host_mac: peer.mac,
      designation,
      suggested_name: peer.host_name || peer.ip || peer.mac,
    });
    if (!resp.ok && resp.error !== 'endpoint_not_deployed') {
      // Surface failures via the import-result banner shape — quick and
      // matches how typed errors render elsewhere on the card. Roll the
      // optimistic state back so the menu doesn't lie.
      setLocalDesignations(prev => {
        const next = new Map(prev);
        next.set(peer.mac, peer.designation ?? null);
        return next;
      });
      setImportError(translatePortForwardError(t, resp.error, resp.detail || null));
    }
  };

  const doResetDesignation = async (peer: DiscoveredPeer) => {
    // Same optimistic pattern as doDesignate. Set the override to null
    // so the menu hides its checkmarks immediately; the next probe
    // will materialise the BE's reverted state (heuristic kind only).
    const previous = peer.designation ?? null;
    setLocalDesignations(prev => {
      const next = new Map(prev);
      next.set(peer.mac, null);
      return next;
    });
    const resp = await clearRouterHostDesignation(zoneId, {
      lan_host_mac: peer.mac,
    });
    if (!resp.ok && resp.error !== 'endpoint_not_deployed') {
      setLocalDesignations(prev => {
        const next = new Map(prev);
        next.set(peer.mac, previous);
        return next;
      });
      setImportError(translatePortForwardError(t, resp.error, resp.detail || null));
    }
  };

  // Connectivity-check semantics shift (coordinated with BE-B): the
  // router-side verifier only meaningfully tests a peer when there's a
  // forwarding path the router knows about. Hosts WITHOUT a 554 forward
  // get no buttons + a hint to create one first; hosts WITH a forward
  // get the full kinds set, and the wire payload uses the new
  // `target: forward_index` shape for rtsp_stream.
  const checkKindsForPeer = (peer: DiscoveredPeer): readonly RouterCheckKind[] => {
    if (!peer.importable_forward) return [];
    const k = effectiveKind(peer);
    return k === 'camera' || k === 'nvr'
      ? (['ping', 'tcp_open', 'rtsp_stream'] as const)
      : (['ping', 'tcp_open'] as const);
  };

  const doCheck = async (
    peer: DiscoveredPeer,
    kind: RouterCheckKind,
  ): Promise<RouterCheckResponse> => {
    // New BE-B contract: discriminated `target` so the router-side
    // verifier knows whether to ping the LAN ip, hit a forward by index,
    // or probe an external endpoint. When the peer has a 554 forward and
    // the kind is rtsp_stream, the forward_index path is the most
    // reliable signal (the router knows the routing path even when its
    // own LAN DHCP table is stale). Fallback for everything else: the
    // raw LAN ip — the API client transparently falls back to the old
    // lan_host shape if BE-B isn't deployed yet.
    const fwd = peer.importable_forward;
    const target: RouterCheckTarget =
      kind === 'rtsp_stream' && fwd?.index
        ? { kind: 'forward_index', value: fwd.index }
        : { kind: 'lan_host', value: peer.ip };
    return checkRouterConnectivity(zoneId, {
      target,
      lan_host: peer.ip,
      kind,
      ...(kind === 'tcp_open' ? { port: 80 } : {}),
      ...(kind === 'rtsp_stream' && fwd
        ? { port: fwd.external_port ?? 554 }
        : {}),
    });
  };

  const submitImport = async () => {
    if (!importTarget) return;
    // Defensive debounce — the Submit button is disabled while busy, but
    // a duplicate submit can still arrive via Enter-key-on-form before
    // React commits the disabled state. Bail early on re-entrance.
    if (importBusy) return;
    setImportBusy(true);
    setImportError(null);
    try {
      let externalPort: number | undefined;
      let internalPort: number = 554;

      // Step 1 — auto-allocate the forward if there isn't one yet. We
      // MUST await this AND consume its returned external_port — finding
      // #25 was an orphan Camera row pointing at 10.99.0.x:8554 because
      // the FE hardcoded 8554 when /forwards/auto/ silently no-op'd.
      // Hard guard below ensures Step 2 never runs without a real port.
      if (importTarget.mode === 'auto-forward') {
        const auto: RouterAutoForwardResponse = await autoCreateRouterForward(zoneId, {
          lan_host_mac: importTarget.peer.mac,
          internal_port: 554,
          family: 'rtsp',
        });
        if (auto.ok) {
          // Prefer the BE-returned external_port; fall back to the
          // forward record nested inside the OK response so a stray
          // missing field at the top level still surfaces the routing
          // path. If BOTH are missing, treat as failure (the BE owes
          // us at least one), not as "use 554".
          externalPort = auto.external_port ?? auto.forward?.external_port ?? undefined;
          internalPort = auto.forward?.internal_port ?? 554;
          if (typeof externalPort !== 'number' || externalPort <= 0) {
            setImportError(t('equipment.discoveredPeer.import.error.autoForwardNoPort'));
            setImportBusy(false);
            return;
          }
        } else if (auto.error === 'no_reachable_path') {
          setImportError(t('equipment.discoveredPeer.import.error.no_reachable_path'));
          setImportBusy(false);
          return;
        } else if (auto.error === 'endpoint_not_deployed') {
          // Surface a toast-style hint but don't proceed — operator must
          // fall back to the manual port-forwards table. The spec's "ship
          // the FE with a clearly-flagged fallback" rule.
          setImportError(t('equipment.discoveredPeer.import.error.endpoint_not_deployed'));
          setImportBusy(false);
          return;
        } else {
          setImportError(translatePortForwardError(t, auto.error, auto.detail));
          setImportBusy(false);
          return;
        }
      } else {
        const fwd = importTarget.peer.importable_forward!;
        externalPort = fwd.external_port ?? undefined;
        internalPort = fwd.internal_port ?? 554;
      }

      // Step 1.5 — hard guard. Refuse to import a Camera row that points
      // at a port the router can't route. Belt-and-braces against the
      // orphan-camera regression: if we somehow got here without a
      // numeric external port, ABORT the Camera creation entirely.
      if (typeof externalPort !== 'number' || externalPort <= 0) {
        setImportError(t('equipment.discoveredPeer.import.error.autoForwardNoPort'));
        setImportBusy(false);
        return;
      }

      // Step 2 — import the camera. Username is intentionally left blank
      // when the operator didn't type one (finding #13); the BE persists
      // the row as inactive and the operator fills creds later in the
      // Camera edit dialog. The router admin's username MUST NOT bleed
      // into the per-camera provider record.
      const resp = await importRouterCameras(zoneId, {
        username: credUsername.trim(),
        password: credPassword,
        idempotency_key: importIdempotencyKey || undefined,
        cameras: [
          {
            name: importTarget.peer.host_name || importTarget.peer.ip || importTarget.peer.mac,
            external_port: externalPort,
            internal_port: internalPort,
            lan_ip: importTarget.peer.ip,
            mac: importTarget.peer.mac,
            hostname: importTarget.peer.host_name,
            type: cameraType,
          },
        ],
      });
      setImportResult(resp.data);
      if (resp.data.ok || resp.data.created.length > 0) {
        onCamerasImported?.();
        setImportTarget(null);
      }
    } catch (err) {
      // Locale-aware error surface (finding #20): in EN we drop Russian-
      // only BE strings on the floor + fall back to the i18n generic.
      setImportError(
        extractApiErrorLocalised(
          isAxiosError(err) ? err : undefined,
          err instanceof Error && lang === 'ru'
            ? err.message
            : t('equipment.discoveredPeer.import.error.generic'),
          lang,
        ),
      );
    } finally {
      setImportBusy(false);
    }
  };

  if (candidates.length === 0 && others.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="router-probe-content">
      {candidates.length > 0 && (
        <DiscoveredPeerList
          source="router-host"
          // Empty title — the parent's CollapsibleSection already carries
          // the «Возможные камеры» header; double-titling reads as visual
          // noise (caught in the 2026-06-02 deploy preview).
          title=""
          helpText={t('equipment.router.probe.suggestedCamerasHelp')}
          peers={candidates}
          selectionMode={bulkA}
          selectedMacs={selectedA}
          onToggleSelectionMode={() => {
            setBulkA(v => !v);
            if (bulkA) setSelectedA(new Set());
          }}
          onToggleSelect={toggleSelectA}
          onImport={startImport}
          onDesignate={doDesignate}
          onResetDesignation={doResetDesignation}
          onCheck={doCheck}
          checkKindsFor={checkKindsForPeer}
        />
      )}

      {bulkA && selectedA.size > 0 && (
        <DiscoveredPeerBulkFooter
          selectedCount={selectedA.size}
          onCancel={() => { setBulkA(false); setSelectedA(new Set()); }}
          onImportCameras={() => {
            // Bulk import — for now we open the legacy multi-import
            // dialog by deferring to the per-row flow on the first
            // selected peer; the legacy bulk modal was kept in place
            // upstream by `<LegacyBulkImportDialog>` below.
            const first = candidates.find(c => selectedA.has(c.mac));
            if (first) startImport(first);
          }}
        />
      )}

      {others.length > 0 && (
        <details className="group rounded-lg border border-border bg-card/40" data-testid="router-section-others">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/80">
              {t('equipment.router.sectionDevices.othersTitle')}
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                {others.length}
              </span>
            </span>
            <svg className="size-4 text-muted-foreground transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div className="border-t border-border px-3 py-3">
            <DiscoveredPeerList
              source="router-host"
              title=""
              helpText={t('equipment.router.sectionDevices.othersHelp')}
              peers={others}
              selectionMode={bulkB}
              selectedMacs={selectedB}
              onToggleSelectionMode={() => {
                setBulkB(v => !v);
                if (bulkB) setSelectedB(new Set());
              }}
              onToggleSelect={toggleSelectB}
              onImport={startImport}
              onDesignate={doDesignate}
              onResetDesignation={doResetDesignation}
              onCheck={doCheck}
              checkKindsFor={checkKindsForPeer}
            />
          </div>
        </details>
      )}

      {bulkB && selectedB.size > 0 && (
        <DiscoveredPeerBulkFooter
          selectedCount={selectedB.size}
          onCancel={() => { setBulkB(false); setSelectedB(new Set()); }}
          onImportCameras={() => {
            const first = others.find(c => selectedB.has(c.mac));
            if (first) startImport(first);
          }}
        />
      )}

      <PerHostImportDialog
        target={importTarget}
        zoneId={zoneId}
        existingForwards={probe.existing_forwards || []}
        wgReady={wgReady}
        wgUplinkIp={probe.wg_uplink_ip}
        busy={importBusy}
        error={importError}
        result={importResult}
        username={credUsername}
        password={credPassword}
        cameraType={cameraType}
        onCameraTypeChange={setCameraType}
        onImported={() => onCamerasImported?.()}
        onUsernameChange={setCredUsername}
        onPasswordChange={setCredPassword}
        onCancel={() => {
          setImportTarget(null);
          setImportError(null);
          setImportResult(null);
        }}
        onSubmit={submitImport}
      />
    </div>
  );
}

/**
 * Per-host import confirm dialog. Hosts both modes:
 *   - existing forward → simple "будет добавлена камера {ip}:{port}".
 *   - auto-forward → "Создадим проброс N → 554 на {iface}. Камера будет
 *     добавлена как неактивная — заполните логин и пароль…".
 *
 * Credentials are optional in the confirm (defaults to admin/empty) so
 * the inactive-import path stays one-click; the operator can also fill
 * them in here if they happen to know them, in which case the BE flags
 * the camera as active immediately.
 */
function PerHostImportDialog({
  target,
  zoneId,
  existingForwards,
  wgReady,
  wgUplinkIp,
  busy,
  error,
  result,
  username,
  password,
  cameraType,
  onCameraTypeChange,
  onImported,
  onUsernameChange,
  onPasswordChange,
  onCancel,
  onSubmit,
}: {
  target: { peer: DiscoveredPeer; mode: 'existing-forward' | 'auto-forward' } | null;
  zoneId: number | string;
  existingForwards: RouterProbeForward[];
  wgReady: boolean;
  wgUplinkIp: string | null;
  busy: boolean;
  error: string | null;
  result: RouterImportCameraResult | null;
  username: string;
  password: string;
  cameraType: string;
  onCameraTypeChange: (v: string) => void;
  onImported: () => void;
  onUsernameChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const { t } = useT();
  const open = !!target;
  const isEzviz = cameraType === 'ezviz';
  // Tracks whether the EZVIZ retrieval card finished all four steps, so
  // the footer can flip from "Закрыть" to a prominent "Готово" — making
  // it obvious the camera was actually added. Reset whenever the dialog
  // re-targets a different host.
  const [ezvizDone, setEzvizDone] = useState(false);
  useEffect(() => { setEzvizDone(false); }, [target]);
  const ifaceLabel = wgReady ? 'Wireguard0' : 'WAN';
  const fwd = target?.peer.importable_forward;
  // Routing-path display (finding #17). The persisted Camera/Provider
  // points at the router's reachable IP — wg_uplink_ip when WG is set
  // up, otherwise the per-host modal can't render the full path and
  // falls back to the LAN ip:port pair only.
  const storedEndpoint =
    wgReady && wgUplinkIp && fwd?.external_port
      ? `${wgUplinkIp}:${fwd.external_port}`
      : null;
  const routerPath =
    fwd?.external_port != null && fwd?.internal_port != null
      ? `${target?.peer.ip}:${fwd.internal_port}, ${fwd.external_port}→${fwd.internal_port}`
      : null;
  const portLabel =
    target?.mode === 'existing-forward' && fwd
      ? `${target.peer.ip}:${fwd.external_port}`
      : target?.peer.ip || '';
  const isAuto = target?.mode === 'auto-forward';
  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v && !busy) onCancel();
      }}
    >
      <DialogContent className="relative">
        <DialogHeader>
          <DialogTitle>
            {isAuto
              ? t('equipment.discoveredPeer.import.confirm.autoForward.title')
              : t('equipment.discoveredPeer.import.confirm.existingForward.title')}
          </DialogTitle>
        </DialogHeader>
        {target && (
          <div className="space-y-3 text-sm">
            <p className="leading-relaxed text-muted-foreground">
              {isAuto
                ? t('equipment.discoveredPeer.import.confirm.autoForward.bodyPending').replace(
                    '{{iface}}',
                    ifaceLabel,
                  )
                : t('equipment.discoveredPeer.import.confirm.existingForward.body').replace(
                    '{{addr}}',
                    portLabel,
                  )}
            </p>
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 font-mono text-xs">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="tabular-nums">{target.peer.ip}</span>
                <span className="text-muted-foreground/60">·</span>
                <span className="tabular-nums text-muted-foreground">{target.peer.mac}</span>
                {target.peer.host_name && (
                  <>
                    <span className="text-muted-foreground/60">·</span>
                    <span className="font-sans text-foreground/80">{target.peer.host_name}</span>
                  </>
                )}
              </div>
            </div>
            {/* Source type — same list as the edit dialog's «Тип
                источника». Picking EZVIZ swaps the confirm action for the
                SDK-bootstrap retrieval card below. */}
            <div className="grid gap-1.5">
              <Label htmlFor="phi-type">{t('equipment.discoveredPeer.import.sourceType')}</Label>
              <Select
                id="phi-type"
                value={cameraType}
                onChange={e => onCameraTypeChange(e.target.value)}
                disabled={busy}
              >
                {CAMERA_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </div>
            {/* Storage-vs-routing breakdown (finding #17). Operators want
                to see BOTH what gets persisted into Linza Metrics AND
                the routing path the camera uses to reach there — the
                old single-line "LAN ip:port" copy was misleading because
                what actually got stored was the WG uplink ip:external. */}
            {storedEndpoint && routerPath && (
              <div className="space-y-1 rounded-lg border border-border bg-card/40 px-3 py-2 font-mono text-[11px]">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-sans text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                    {t('equipment.discoveredPeer.import.confirm.storedAs')}
                  </span>
                  <span className="tabular-nums">{storedEndpoint}</span>
                </div>
                <div className="flex flex-wrap items-baseline gap-x-2 text-muted-foreground">
                  <span className="font-sans text-[10px] uppercase tracking-[0.08em] opacity-80">
                    {t('equipment.discoveredPeer.import.confirm.routerPath')}
                  </span>
                  <span className="tabular-nums">{routerPath}</span>
                </div>
              </div>
            )}
            {/* Operator hint — camera credentials are entered AFTER the
                import in the Camera edit dialog (finding #13). The
                router admin credentials must never be the default. */}
            <p className="text-[11px] leading-snug text-muted-foreground">
              {t('equipment.discoveredPeer.import.confirm.credsHelp')}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="phi-user">{t('equipment.router.import.username')}</Label>
                <Input
                  id="phi-user"
                  value={username}
                  onChange={e => onUsernameChange(e.target.value)}
                  className="font-mono"
                  autoComplete="off"
                  placeholder={t('equipment.discoveredPeer.import.confirm.credsLater')}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phi-pw">
                  {isEzviz
                    ? t('equipment.discoveredPeer.import.ezviz.verificationCode')
                    : t('equipment.router.import.password')}
                </Label>
                <Input
                  id="phi-pw"
                  type={isEzviz ? 'text' : 'password'}
                  value={password}
                  onChange={e => onPasswordChange(e.target.value)}
                  className="font-mono"
                  autoComplete="new-password"
                  placeholder={
                    isEzviz
                      ? t('equipment.discoveredPeer.import.ezviz.verificationCodePlaceholder')
                      : t('equipment.discoveredPeer.import.confirm.credsLater')
                  }
                />
              </div>
            </div>
            {isEzviz && target && (
              <EzvizRtspRetrievalCard
                zoneId={zoneId}
                peer={target.peer}
                wgReady={wgReady}
                username={username}
                verificationCode={password}
                existingForwards={existingForwards}
                onImported={onImported}
                onDone={() => setEzvizDone(true)}
              />
            )}
            {error && (
              <div className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive" data-testid="per-host-import-error">
                {error}
              </div>
            )}
            {result && (result.ok || result.created.length > 0) && (
              <div className="rounded-lg bg-[color:var(--color-success)]/10 px-3 py-2 text-xs text-[color:var(--color-success)]">
                <CheckCircle2 className="mr-1 inline size-3" />
                {t('equipment.router.import.successCreated')}: {result.created.length}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button
            variant={isEzviz && ezvizDone ? 'default' : 'outline'}
            size="sm"
            onClick={onCancel}
            disabled={busy}
          >
            {isEzviz
              ? ezvizDone
                ? t('equipment.discoveredPeer.import.ezviz.done')
                : t('equipment.discoveredPeer.import.ezviz.close')
              : t('equipment.discoveredPeer.import.confirm.cancel')}
          </Button>
          {/* EZVIZ drives its own multi-step Run button inside the
              retrieval card; the plain one-shot Submit only applies to
              the legacy direct-import types. */}
          {!isEzviz && (
            <Button
              size="sm"
              onClick={onSubmit}
              disabled={busy}
              data-testid="per-host-import-submit"
            >
              {busy && <Loader2 className="size-3.5 animate-spin" />}
              {t('equipment.discoveredPeer.import.confirm.submit')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * EZVIZ RTSP retrieval card. EZVIZ cameras ship with RTSP disabled, so
 * onboarding one is a pipeline the operator kicks off with a single Run
 * button — re-runnable on failure, because every step is idempotent:
 *
 *   1. Пробросы портов — ensure a 554 (RTSP) and an 8000 (Hik SDK)
 *      forward exist for this camera's MAC. Reuses an existing forward
 *      where present; otherwise auto-allocates the next free external
 *      port per family (rtsp → 8554/9554…, bootstrap → 8800/9800…).
 *   2. Включение RTSP — call the Hik SDK over the forwarded :8000 to
 *      flip the camera's servicesSwitch flags. The BE cache-locks this
 *      so a double-click can't race two SDK sessions (`already_running`).
 *   3. Импорт камеры — create the Camera/provider rows as type `ezviz`.
 *   4. Проба RTSP — probe the stream for resolution + codec and a
 *      preview frame, persisting resolution/codec onto the new row.
 */
function EzvizRtspRetrievalCard({
  zoneId,
  peer,
  wgReady,
  username,
  verificationCode,
  existingForwards,
  onImported,
  onDone,
}: {
  zoneId: number | string;
  peer: DiscoveredPeer;
  wgReady: boolean;
  username: string;
  verificationCode: string;
  existingForwards: RouterProbeForward[];
  onImported: () => void;
  onDone?: () => void;
}) {
  const { t } = useT();
  const cameraName = peer.host_name || peer.ip || peer.mac;

  type StepKey = 'forwards' | 'rtsp' | 'import' | 'probe';
  type StepStatus = 'pending' | 'running' | 'ok' | 'error';
  const STEP_ORDER: StepKey[] = ['forwards', 'rtsp', 'import', 'probe'];

  const [status, setStatus] = useState<Record<StepKey, StepStatus>>({
    forwards: 'pending', rtsp: 'pending', import: 'pending', probe: 'pending',
  });
  const [detail, setDetail] = useState<Partial<Record<StepKey, string>>>({});
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Ports + idempotency key persist across re-runs so a retry reuses the
  // forwards it already created (instead of allocating a SECOND one) and
  // replays the same import idempotency key.
  const portsRef = useRef<{ rtsp?: number; bootstrap?: number }>({});
  const idemRef = useRef<string>(mintUuid());

  // Revoke the snapshot object URL on unmount / replacement.
  useEffect(() => () => { if (imageUrl) URL.revokeObjectURL(imageUrl); }, [imageUrl]);

  const setStep = (k: StepKey, s: StepStatus, d?: string) => {
    setStatus(prev => ({ ...prev, [k]: s }));
    if (d !== undefined) setDetail(prev => ({ ...prev, [k]: d }));
  };

  const findForward = (internalPort: number): number | undefined => {
    const m = (peer.mac || '').toLowerCase();
    const hit = (existingForwards || []).find(
      f => (f.to_host || '').toLowerCase() === m
        && f.internal_port === internalPort
        && (f.external_port ?? 0) > 0,
    );
    return hit?.external_port ?? undefined;
  };

  const ensureForward = async (
    internalPort: number,
    family: 'rtsp' | 'bootstrap',
  ): Promise<number> => {
    const cached = family === 'rtsp' ? portsRef.current.rtsp : portsRef.current.bootstrap;
    if (cached) return cached;
    const existing = findForward(internalPort);
    if (existing) {
      if (family === 'rtsp') portsRef.current.rtsp = existing;
      else portsRef.current.bootstrap = existing;
      return existing;
    }
    const r = await autoCreateRouterForward(zoneId, {
      lan_host_mac: peer.mac, internal_port: internalPort, family,
    });
    if (!r.ok) {
      throw new Error(translatePortForwardError(t, r.error, r.detail));
    }
    const port = r.external_port ?? r.forward?.external_port;
    if (typeof port !== 'number' || port <= 0) {
      throw new Error(t('equipment.discoveredPeer.import.error.autoForwardNoPort'));
    }
    if (family === 'rtsp') portsRef.current.rtsp = port;
    else portsRef.current.bootstrap = port;
    return port;
  };

  const run = async () => {
    if (running) return;
    if (!wgReady) {
      setStep('forwards', 'error', t('equipment.discoveredPeer.import.error.no_reachable_path'));
      return;
    }
    if (!verificationCode.trim()) {
      setStep('rtsp', 'error', t('equipment.discoveredPeer.import.ezviz.needVerificationCode'));
      return;
    }
    setRunning(true);

    // Step 1 — forwards.
    setStep('forwards', 'running');
    let rtspPort: number;
    let bootstrapPort: number;
    try {
      rtspPort = await ensureForward(554, 'rtsp');
      bootstrapPort = await ensureForward(8000, 'bootstrap');
      setStep('forwards', 'ok',
        t('equipment.discoveredPeer.import.ezviz.forwardsDone')
          .replace('{{rtsp}}', String(rtspPort))
          .replace('{{sdk}}', String(bootstrapPort)));
    } catch (e) {
      setStep('forwards', 'error', e instanceof Error ? e.message : String(e));
      setRunning(false);
      return;
    }

    // Step 2 — enable RTSP over the Hik SDK.
    setStep('rtsp', 'running');
    try {
      const b = await ezvizBootstrap(zoneId, {
        external_port: bootstrapPort,
        username: username.trim() || 'admin',
        verification_code: verificationCode,
      });
      if (!b.ok) {
        setStep('rtsp', 'error',
          b.error === 'already_running'
            ? t('equipment.discoveredPeer.import.ezviz.alreadyRunning')
            : (b.detail || t('equipment.discoveredPeer.import.ezviz.bootstrapFailed')));
        setRunning(false);
        return;
      }
      setStep('rtsp', 'ok');
    } catch (e) {
      setStep('rtsp', 'error', e instanceof Error ? e.message : String(e));
      setRunning(false);
      return;
    }

    // Step 3 — import the camera as ezviz.
    setStep('import', 'running');
    let cameraId: number | undefined;
    let providerId: number | undefined;
    try {
      const imp = await importRouterCameras(zoneId, {
        username: username.trim() || 'admin',
        password: verificationCode,
        idempotency_key: idemRef.current,
        cameras: [{
          name: peer.host_name || peer.ip || peer.mac,
          external_port: rtspPort,
          internal_port: 554,
          lan_ip: peer.ip,
          mac: peer.mac,
          hostname: peer.host_name,
          type: 'ezviz',
          // The :8000 SDK/ISAPI forward is EZVIZ's admin port (see
          // admin_ports.for_camera → http:8000), reached via the router
          // WG IP — not a bare :80.
          api_port: bootstrapPort,
        }],
      });
      const created = imp.data.created[0];
      if (created) {
        cameraId = created.camera_id;
        providerId = created.provider_id;
      } else {
        const dup = imp.data.skipped.find(s => s.camera_id);
        if (dup?.camera_id) {
          cameraId = dup.camera_id;
        } else {
          setStep('import', 'error',
            imp.data.errors[0]?.detail || t('equipment.discoveredPeer.import.error.generic'));
          setRunning(false);
          return;
        }
      }
      onImported();
      setStep('import', 'ok');
    } catch (e) {
      setStep('import', 'error', e instanceof Error ? e.message : String(e));
      setRunning(false);
      return;
    }

    // Step 4 — probe resolution + codec, grab a preview frame.
    setStep('probe', 'running');
    try {
      let resolution = '';
      let codec = '';
      if (cameraId) {
        const res = await detectCameraResolution(cameraId);
        const w = res.data.max_x_resolution;
        const h = res.data.max_y_resolution;
        if (w && h) resolution = `${w}×${h}`;
      }
      if (providerId) {
        try {
          const c = await detectProviderCodec(providerId);
          codec = c.data.codec || '';
        } catch {
          // Codec is best-effort — some streams don't expose a known
          // FourCC; resolution + a live frame already prove RTSP works.
        }
      }
      if (cameraId) {
        try {
          const snap = await getCameraSnapshot(cameraId);
          setImageUrl(URL.createObjectURL(snap.data));
        } catch {
          // Preview frame is cosmetic — ignore fetch failures.
        }
      }
      const summary = [resolution, codec].filter(Boolean).join(', ');
      setStep('probe', 'ok', summary || t('equipment.discoveredPeer.import.ezviz.probeNoMeta'));
      setDone(true);
      onDone?.();
    } catch (e) {
      setStep('probe', 'error', e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  const anyError = STEP_ORDER.some(k => status[k] === 'error');

  const STEP_LABELS: Record<StepKey, string> = {
    forwards: t('equipment.discoveredPeer.import.ezviz.step.forwards'),
    rtsp: t('equipment.discoveredPeer.import.ezviz.step.rtsp'),
    import: t('equipment.discoveredPeer.import.ezviz.step.import'),
    probe: t('equipment.discoveredPeer.import.ezviz.step.probe'),
  };

  const runLabel = running
    ? t('equipment.discoveredPeer.import.ezviz.running')
    : (anyError || done)
      ? t('equipment.discoveredPeer.import.ezviz.runAgain')
      : t('equipment.discoveredPeer.import.ezviz.run');

  return (
    <div
      className="space-y-3 rounded-lg border border-border bg-card/40 p-3"
      data-testid="ezviz-retrieval-card"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground/80">
          {t('equipment.discoveredPeer.import.ezviz.title')}
        </div>
        <Button size="sm" onClick={run} disabled={running} data-testid="ezviz-retrieval-run">
          {running
            ? <Loader2 className="size-3.5 animate-spin" />
            : (anyError || done)
              ? <RotateCw className="size-3.5" />
              : <Play className="size-3.5" />}
          {runLabel}
        </Button>
      </div>

      <ol className="space-y-1.5">
        {STEP_ORDER.map((k, i) => {
          const s = status[k];
          return (
            <li key={k} className="flex items-start gap-2 text-xs">
              <span className="mt-0.5 shrink-0">
                {s === 'running'
                  ? <Loader2 className="size-3.5 animate-spin text-primary" />
                  : s === 'ok'
                    ? <CheckCircle2 className="size-3.5 text-[color:var(--color-success)]" />
                    : s === 'error'
                      ? <AlertCircle className="size-3.5 text-destructive" />
                      : <Circle className="size-3.5 text-muted-foreground/50" />}
              </span>
              <div className="space-y-0.5">
                <div className="font-medium text-foreground/90">{i + 1}. {STEP_LABELS[k]}</div>
                {detail[k] && (
                  <div className={cn(
                    'font-mono text-[11px]',
                    s === 'error' ? 'text-destructive' : 'text-muted-foreground',
                  )}>
                    {detail[k]}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {done && (
        <div
          className="flex items-start gap-2 rounded-lg bg-[color:var(--color-success)]/10 px-3 py-2 text-xs text-[color:var(--color-success)]"
          data-testid="ezviz-retrieval-done"
        >
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-0.5">
            <div className="font-medium">
              {t('equipment.discoveredPeer.import.ezviz.successTitle').replace('{{name}}', cameraName)}
            </div>
            <div className="opacity-80">
              {t('equipment.discoveredPeer.import.ezviz.successHint')}
              {detail.probe ? ` · ${detail.probe}` : ''}
            </div>
          </div>
        </div>
      )}

      {imageUrl && (
        <img
          src={imageUrl}
          alt={t('equipment.discoveredPeer.import.ezviz.previewAlt')}
          className="max-h-48 w-full rounded-md border border-border object-contain"
        />
      )}
    </div>
  );
}

/** Searching… placeholder kept for the legacy probe-button. */
export function RouterProbeLoading() {
  const { t } = useT();
  return (
    <div className="flex items-center gap-2 border-t pt-2 text-xs text-muted-foreground">
      <Loader2 className="size-3.5 animate-spin" />
      {t('equipment.router.probe.searching')}
    </div>
  );
}

/** Header model-badge — kept for any consumers still importing it. */
export function RouterProbeModelBadge({ probe }: { probe: RouterProbeOk }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded bg-green-500/15 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:text-green-400',
      )}
    >
      <CheckCircle2 className="size-3" />
      {probe.model || '?'}
      {probe.firmware_version && ` · v${probe.firmware_version}`}
    </span>
  );
}
